import React, { useState } from 'react';
import { FileTextIcon, PaperclipIcon, PillIcon, FlaskConicalIcon, SearchIcon } from 'lucide-react';
interface ConsultationWorkflowProps {
  doctorId: number;
}
const mockPatients = [{
  id: 1,
  name: 'John Doe',
  age: 45,
  lastVisit: '2024-01-10',
  status: 'Active'
}, {
  id: 2,
  name: 'Jane Smith',
  age: 32,
  lastVisit: '2024-01-12',
  status: 'Active'
}, {
  id: 3,
  name: 'Bob Wilson',
  age: 58,
  lastVisit: '2024-01-08',
  status: 'Active'
}];
export function ConsultationWorkflow({
  doctorId
}: ConsultationWorkflowProps) {
  const [selectedPatient, setSelectedPatient] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'notes' | 'attachments' | 'prescriptions' | 'tests'>('notes');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [prescriptions, setPrescriptions] = useState([{
    id: 1,
    medication: 'Lisinopril 10mg',
    dosage: '1 tablet daily',
    duration: '30 days'
  }]);
  const [testOrders, setTestOrders] = useState([{
    id: 1,
    testName: 'Complete Blood Count',
    priority: 'Routine',
    notes: 'Fasting required'
  }]);
  const filteredPatients = mockPatients.filter(patient => patient.name.toLowerCase().includes(searchTerm.toLowerCase()));
  return <div className="space-y-6" data-id="element-253">
      <h2 className="text-xl font-bold text-gray-900" data-id="element-254">Consultation Workflow</h2>
      {/* Patient Search & Selection */}
      <div className="border border-gray-200 rounded-lg p-4" data-id="element-255">
        <div className="relative mb-4" data-id="element-256">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-257" />
          <input type="text" placeholder="Search patient by name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-258" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-259">
          {filteredPatients.map(patient => <button key={patient.id} onClick={() => setSelectedPatient(patient.id)} className={`p-4 border-2 rounded-lg text-left transition-all ${selectedPatient === patient.id ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-260">
              <h4 className="font-semibold text-gray-900" data-id="element-261">{patient.name}</h4>
              <p className="text-sm text-gray-600" data-id="element-262">Age: {patient.age}</p>
              <p className="text-xs text-gray-500" data-id="element-263">
                Last visit: {patient.lastVisit}
              </p>
            </button>)}
        </div>
      </div>
      {selectedPatient ? <>
          {/* Consultation Tabs */}
          <div className="border border-gray-200 rounded-lg" data-id="element-264">
            <div className="border-b border-gray-200" data-id="element-265">
              <div className="flex overflow-x-auto" data-id="element-266">
                <button onClick={() => setActiveTab('notes')} className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'notes' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-267">
                  <FileTextIcon className="w-4 h-4 inline mr-2" data-id="element-268" />
                  Clinical Notes
                </button>
                <button onClick={() => setActiveTab('attachments')} className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'attachments' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-269">
                  <PaperclipIcon className="w-4 h-4 inline mr-2" data-id="element-270" />
                  Attachments
                </button>
                <button onClick={() => setActiveTab('prescriptions')} className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'prescriptions' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-271">
                  <PillIcon className="w-4 h-4 inline mr-2" data-id="element-272" />
                  E-Prescriptions
                </button>
                <button onClick={() => setActiveTab('tests')} className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'tests' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-273">
                  <FlaskConicalIcon className="w-4 h-4 inline mr-2" data-id="element-274" />
                  Test Orders
                </button>
              </div>
            </div>
            <div className="p-6" data-id="element-275">
              {/* Clinical Notes */}
              {activeTab === 'notes' && <div className="space-y-4" data-id="element-276">
                  <div data-id="element-277">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-278">
                      Clinical Notes for{' '}
                      {mockPatients.find(p => p.id === selectedPatient)?.name}
                    </label>
                    <textarea value={clinicalNotes} onChange={e => setClinicalNotes(e.target.value)} rows={12} placeholder="Enter clinical observations, diagnosis, treatment plan..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-279" />
                  </div>
                  <div className="flex justify-end" data-id="element-280">
                    <button className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-281">
                      Save Notes
                    </button>
                  </div>
                </div>}
              {/* Attachments */}
              {activeTab === 'attachments' && <div className="space-y-4" data-id="element-282">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-283">
                    <PaperclipIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" data-id="element-284" />
                    <p className="text-gray-600 mb-2" data-id="element-285">
                      Upload medical reports, images, or documents
                    </p>
                    <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-286">
                      Choose Files
                    </button>
                  </div>
                  <div className="space-y-2" data-id="element-287">
                    <h4 className="font-medium text-gray-900" data-id="element-288">
                      Previous Attachments
                    </h4>
                    <div className="p-4 border border-gray-200 rounded-lg flex items-center justify-between" data-id="element-289">
                      <div className="flex items-center space-x-3" data-id="element-290">
                        <PaperclipIcon className="w-5 h-5 text-gray-400" data-id="element-291" />
                        <div data-id="element-292">
                          <p className="font-medium text-gray-900" data-id="element-293">
                            ECG Report.pdf
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-294">
                            Uploaded on 2024-01-10
                          </p>
                        </div>
                      </div>
                      <button className="text-purple-600 hover:text-purple-700 text-sm font-medium" data-id="element-295">
                        View
                      </button>
                    </div>
                  </div>
                </div>}
              {/* E-Prescriptions */}
              {activeTab === 'prescriptions' && <div className="space-y-4" data-id="element-296">
                  <div className="flex items-center justify-between" data-id="element-297">
                    <h4 className="font-medium text-gray-900" data-id="element-298">
                      E-Prescriptions
                    </h4>
                    <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm" data-id="element-299">
                      Add Medication
                    </button>
                  </div>
                  <div className="space-y-3" data-id="element-300">
                    {prescriptions.map(prescription => <div key={prescription.id} className="p-4 border border-gray-200 rounded-lg" data-id="element-301">
                        <div className="flex items-start justify-between" data-id="element-302">
                          <div data-id="element-303">
                            <h5 className="font-semibold text-gray-900" data-id="element-304">
                              {prescription.medication}
                            </h5>
                            <p className="text-sm text-gray-600" data-id="element-305">
                              Dosage: {prescription.dosage}
                            </p>
                            <p className="text-sm text-gray-600" data-id="element-306">
                              Duration: {prescription.duration}
                            </p>
                          </div>
                          <button className="text-red-600 hover:text-red-700 text-sm" data-id="element-307">
                            Remove
                          </button>
                        </div>
                      </div>)}
                  </div>
                  <div className="flex justify-end space-x-3" data-id="element-308">
                    <button className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-id="element-309">
                      Save Draft
                    </button>
                    <button className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-310">
                      Generate Prescription
                    </button>
                  </div>
                </div>}
              {/* Test Orders */}
              {activeTab === 'tests' && <div className="space-y-4" data-id="element-311">
                  <div className="flex items-center justify-between" data-id="element-312">
                    <h4 className="font-medium text-gray-900" data-id="element-313">
                      Laboratory Test Orders
                    </h4>
                    <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm" data-id="element-314">
                      Add Test
                    </button>
                  </div>
                  <div className="space-y-3" data-id="element-315">
                    {testOrders.map(test => <div key={test.id} className="p-4 border border-gray-200 rounded-lg" data-id="element-316">
                        <div className="flex items-start justify-between" data-id="element-317">
                          <div data-id="element-318">
                            <h5 className="font-semibold text-gray-900" data-id="element-319">
                              {test.testName}
                            </h5>
                            <p className="text-sm text-gray-600" data-id="element-320">
                              Priority: {test.priority}
                            </p>
                            <p className="text-sm text-gray-600" data-id="element-321">
                              Notes: {test.notes}
                            </p>
                          </div>
                          <button className="text-red-600 hover:text-red-700 text-sm" data-id="element-322">
                            Remove
                          </button>
                        </div>
                      </div>)}
                  </div>
                  <div className="flex justify-end" data-id="element-323">
                    <button className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-324">
                      Submit Test Orders
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </> : <div className="border border-gray-200 rounded-lg p-12 text-center" data-id="element-325">
          <FileTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-326" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-327">
            No Patient Selected
          </h3>
          <p className="text-gray-500" data-id="element-328">
            Select a patient to start consultation workflow
          </p>
        </div>}
    </div>;
}